/**
 * Tressette bot.
 *
 * Plays from the bot's own view only:
 *  - Leading: throw the cheapest card (a zero-point low card of the longest suit); keep aces and the big cards back.
 *    A card nobody can still beat (everything higher is already played) is a safe lead, so it is played to win points.
 *  - Partner already winning: load the trick with points ("carico") once it is safe, otherwise play low.
 *  - Opponent winning: take the trick with the cheapest card that wins when it is worth it (it carries points, or it is
 *    the last trick), otherwise give up the cheapest card.
 *  - HARD/EXPERT/NORMAL count the cards played so far to tell whether the partner's card can still be beaten.
 */
import { parseCard, type CardId, type Suit } from '../../core/cards.js';
import { chance, type BotContext, type BotEngine } from '../../bots/types.js';
import {
  tressetteDeck,
  tressettePartnerOf,
  tressetteStrength,
  tressetteThirds,
  tressetteTrickWinner,
  type TressetteAction,
  type TressetteLegal,
  type TressetteTrickPlay,
  type TressetteView,
} from './engine.js';

const suitOf = (id: CardId): Suit => parseCard(id).suit;

/** How much it hurts to give a card away: plain zero-point cards are cheapest, point cards and big cards dearest. */
const cost = (c: CardId): number => tressetteThirds(c) * 20 + tressetteStrength(c);
const cheapest = (cards: readonly CardId[]): CardId => [...cards].sort((a, b) => cost(a) - cost(b))[0]!;

function unseenCards(view: TressetteView, ctx: BotContext): Set<CardId> {
  const seen = new Set<CardId>(view.hand);
  view.trick.forEach((p) => seen.add(p.card));
  const n = view.tricks.length;
  const keep = ctx.profile.memory >= 1 ? n : Math.round(n * ctx.profile.memory);
  view.tricks.forEach((t, i) => {
    if (i >= n - keep) for (const p of t.plays) seen.add(p.card);
  });
  return new Set(tressetteDeck().filter((c) => !seen.has(c)));
}

function wouldWin(trick: readonly TressetteTrickPlay[], seat: number, card: CardId): boolean {
  if (trick.length === 0) return true;
  return tressetteTrickWinner([...trick, { seat, card }]) === seat;
}

/** True when no unseen card of the same suit could still beat `card`. */
function isUnbeatable(card: CardId, unseen: Set<CardId>): boolean {
  for (const u of unseen) if (suitOf(u) === suitOf(card) && tressetteStrength(u) > tressetteStrength(card)) return false;
  return true;
}

function lead(view: TressetteView, legal: CardId[], ctx: BotContext): CardId {
  const unseen = ctx.profile.memory > 0 ? unseenCards(view, ctx) : null;
  if (unseen) {
    // A card nobody can beat wins the trick: cash the one that carries the most points.
    const safe = legal.filter((c) => isUnbeatable(c, unseen)).sort((a, b) => tressetteThirds(b) - tressetteThirds(a) || tressetteStrength(a) - tressetteStrength(b));
    if (safe.length && (tressetteThirds(safe[0]!) > 0 || safe.length === legal.length)) return safe[0]!;
  }
  const free = legal.filter((c) => tressetteThirds(c) === 0);
  if (free.length) {
    // Lead low from the longest suit, so the high cards stay for winning tricks later.
    const length = (s: Suit) => legal.filter((c) => suitOf(c) === s).length;
    return [...free].sort((a, b) => length(suitOf(b)) - length(suitOf(a)) || tressetteStrength(a) - tressetteStrength(b))[0]!;
  }
  return cheapest(legal);
}

function follow(view: TressetteView, legal: CardId[], ctx: BotContext): CardId {
  const me = view.seat!;
  const trick = view.trick;
  const partner = tressettePartnerOf(me);
  const winnerSeat = tressetteTrickWinner(trick);
  const winnerCard = trick.find((p) => p.seat === winnerSeat)!.card;
  const onTable = trick.reduce((n, p) => n + tressetteThirds(p.card), 0);
  const last = trick.length === 3;
  const finalTrick = view.tricks.length === 9;
  const unseen = ctx.profile.memory > 0 ? unseenCards(view, ctx) : null;
  const winners = legal.filter((c) => wouldWin(trick, me, c));

  if (winnerSeat === partner) {
    const safe = last || (unseen ? isUnbeatable(winnerCard, unseen) : tressetteStrength(winnerCard) >= 9);
    if (safe) {
      // Load the trick with points, giving the most valuable card the rules allow.
      const loads = legal.filter((c) => tressetteThirds(c) > 0);
      if (loads.length) return [...loads].sort((a, b) => tressetteThirds(b) - tressetteThirds(a) || tressetteStrength(a) - tressetteStrength(b))[0]!;
    }
    return cheapest(legal);
  }

  // An opponent has the trick: take it when it carries points (or is the last trick), with the cheapest card that wins.
  if (winners.length > 0 && (onTable > 0 || finalTrick || (last && tressetteThirds(winners[0]!) > 0))) return cheapest(winners);
  if (winners.length > 0 && last) return cheapest(winners);
  return cheapest(legal);
}

export function tressetteBotChoose(view: TressetteView, legal: CardId[], ctx: BotContext): CardId {
  if (legal.length === 1) return legal[0]!;
  return view.trick.length === 0 ? lead(view, legal, ctx) : follow(view, legal, ctx);
}

export const tressetteBot: BotEngine<TressetteView, TressetteLegal, TressetteAction> = {
  decideAction(view, legal, ctx) {
    if (legal.kind === 'play') return this.chooseCard!(view, legal, ctx);
    throw new Error('Bot asked to act without legal actions');
  },

  chooseCard(view, legal, ctx) {
    if (legal.kind !== 'play') return this.handleTimeout(view, legal, ctx);
    const cards = legal.cards;
    if (cards.length === 1) return { type: 'play', card: cards[0]! };
    if (chance(ctx, ctx.profile.mistakeRate)) return { type: 'play', card: cards[ctx.rng.int(cards.length)]! };
    const card = tressetteBotChoose(view, cards, ctx);
    return { type: 'play', card: cards.includes(card) ? card : cards[0]! };
  },

  evaluateState(view) {
    const me = view.seat;
    if (me === null) return 0;
    const team = me % 2;
    const diff = view.teamThirds[team]! - view.teamThirds[1 - team]!;
    return Math.max(0, Math.min(1, 0.5 + diff / 70));
  },

  calculateRisk() {
    return 0;
  },

  handleTimeout(_view, legal) {
    if (legal.kind === 'play') return { type: 'play', card: cheapest(legal.cards) };
    throw new Error('No legal action');
  },
};
